import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { KPICard } from '../components/common/KPICard';
import { StatusBadge } from '../components/common/StatusBadge';
import { PriorityBadge } from '../components/common/PriorityBadge';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
    Truck, 
    ClipboardList, 
    Users,
    Bell,
    ArrowRight,
    AlertTriangle,
    CheckCircle2,
    Clock,
    Wrench,
    Fuel,
    Route,
    FileWarning
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getDashboardStats, getRecentActivities, getMonthlyStats, getMaintenanceCosts, seedDemoData } from '../lib/api';
import { formatRelativeTime } from '../lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { toast } from 'sonner';

const CHART_COLORS = ['#F97316', '#334155', '#10B981', '#F59E0B', '#0EA5E9'];

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [activities, setActivities] = useState([]);
    const [monthlyData, setMonthlyData] = useState([]);
    const [costData, setCostData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [seeding, setSeeding] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const [statsRes, activitiesRes, monthlyRes, costRes] = await Promise.all([
                getDashboardStats(),
                getRecentActivities(),
                getMonthlyStats(),
                getMaintenanceCosts()
            ]);
            setStats(statsRes.data);
            setActivities(activitiesRes.data);
            setMonthlyData(monthlyRes.data);
            setCostData(costRes.data);
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
            toast.error('Gagal memuat data dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleSeedData = async () => {
        try {
            setSeeding(true);
            await seedDemoData();
            toast.success('Demo data berhasil dimuat');
            fetchDashboardData();
        } catch (error) {
            console.error('Failed to seed data:', error);
            toast.error('Gagal memuat demo data');
        } finally {
            setSeeding(false);
        }
    };

    const statusDistribution = stats ? [
        { name: 'Pending', value: stats.work_orders.pending, color: '#F59E0B' },
        { name: 'In Progress', value: stats.work_orders.in_progress, color: '#0EA5E9' },
        { name: 'Completed', value: stats.work_orders.completed, color: '#10B981' }
    ].filter(item => item.value > 0) : [];

    if (loading) {
        return (
            <Layout title="Dashboard">
                <LoadingSpinner size="large" />
            </Layout>
        );
    }

    const isEmpty = stats && stats.armada.total === 0;

    return (
        <Layout title="Dashboard">
            <div className="space-y-8" data-testid="dashboard-page">
                {isEmpty && (
                    <Card className="bg-primary/5 border-primary/20">
                        <CardContent className="flex items-center justify-between p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center">
                                    <Fuel className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-semibold uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                                        Selamat Datang di Zenith Asset Intelligence
                                    </h3>
                                    <p className="text-sm text-muted-foreground">
                                        Sistem Manajemen Armada Pengiriman Solar Industry
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <Button 
                                    onClick={handleSeedData} 
                                    disabled={seeding}
                                    className="industrial-btn-primary"
                                    data-testid="seed-data-btn"
                                >
                                    {seeding ? 'Memuat...' : 'Muat Demo Data'}
                                </Button>
                                <Button 
                                    variant="outline" 
                                    onClick={() => navigate('/vehicles')}
                                    className="industrial-btn-outline"
                                    data-testid="add-vehicle-btn"
                                >
                                    Tambah Armada
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* KPI Cards - Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <KPICard
                        title="Total Armada"
                        value={stats?.armada.total || 0}
                        subtitle={`${stats?.armada.operasional || 0} operasional`}
                        icon={Truck}
                    />
                    <KPICard
                        title="Work Orders"
                        value={stats?.work_orders.total || 0}
                        subtitle={`${stats?.work_orders.pending || 0} pending`}
                        icon={ClipboardList}
                    />
                    <KPICard
                        title="Sopir Aktif"
                        value={stats?.drivers.aktif || 0}
                        subtitle={`dari ${stats?.drivers.total || 0} total`}
                        icon={Users}
                    />
                    <KPICard
                        title="Notifikasi"
                        value={stats?.notifications.unread || 0}
                        subtitle={`${stats?.notifications.dokumen_expired_soon || 0} dokumen segera expired`}
                        icon={Bell}
                    />
                </div>

                {/* KPI Cards - Row 2 (Bulan Ini) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <KPICard
                        title="Trip Bulan Ini"
                        value={stats?.bulan_ini.total_trip || 0}
                        subtitle="Total pengiriman"
                        icon={Route}
                    />
                    <KPICard
                        title="Total KM Bulan Ini"
                        value={stats?.bulan_ini.total_km?.toLocaleString() || 0}
                        subtitle="Kilometer"
                        icon={Truck}
                    />
                    <KPICard
                        title="Volume Solar Dikirim"
                        value={stats?.bulan_ini.total_volume_liter?.toLocaleString() || 0}
                        subtitle="Liter bulan ini"
                        icon={Fuel}
                    />
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Monthly Trend Chart */}
                    <Card className="industrial-card">
                        <CardHeader className="border-b border-border pb-4">
                            <CardTitle className="section-title text-lg">Trend Bulanan</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <div className="h-[300px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={monthlyData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                        <XAxis 
                                            dataKey="month" 
                                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                                        />
                                        <YAxis 
                                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                                        />
                                        <Tooltip 
                                            contentStyle={{
                                                backgroundColor: 'hsl(var(--card))',
                                                border: '1px solid hsl(var(--border))',
                                                borderRadius: '0.25rem'
                                            }}
                                        />
                                        <Legend />
                                        <Bar dataKey="wo_created" fill="#334155" name="WO Dibuat" radius={[2, 2, 0, 0]} />
                                        <Bar dataKey="wo_completed" fill="#10B981" name="WO Selesai" radius={[2, 2, 0, 0]} />
                                        <Bar dataKey="trips" fill="#F97316" name="Trip" radius={[2, 2, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Maintenance Cost Trend */}
                    <Card className="industrial-card">
                        <CardHeader className="border-b border-border pb-4">
                            <CardTitle className="section-title text-lg">Biaya Maintenance</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <div className="h-[300px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={costData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                        <XAxis 
                                            dataKey="month" 
                                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                                        />
                                        <YAxis 
                                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                                            tickFormatter={(value) => `${(value / 1000000).toFixed(1)}jt`}
                                        />
                                        <Tooltip 
                                            contentStyle={{
                                                backgroundColor: 'hsl(var(--card))',
                                                border: '1px solid hsl(var(--border))',
                                                borderRadius: '0.25rem'
                                            }}
                                            formatter={(value) => [`Rp ${value.toLocaleString()}`, 'Biaya']}
                                        />
                                        <Line type="monotone" dataKey="biaya" stroke="#F97316" strokeWidth={2} dot={{ fill: '#F97316' }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Quick Status & Recent Activities */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Quick Status */}
                    <Card className="industrial-card">
                        <CardHeader className="border-b border-border pb-4">
                            <CardTitle className="section-title text-lg">Status Armada</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-sm border border-green-200 dark:border-green-800">
                                    <div className="flex items-center gap-3">
                                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                                        <span className="text-sm font-medium">Operasional</span>
                                    </div>
                                    <span className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                                        {stats?.armada.operasional || 0}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-sm border border-blue-200 dark:border-blue-800">
                                    <div className="flex items-center gap-3">
                                        <Wrench className="w-5 h-5 text-blue-600" />
                                        <span className="text-sm font-medium">Maintenance</span>
                                    </div>
                                    <span className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                                        {stats?.armada.maintenance || 0}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/20 rounded-sm border border-gray-200 dark:border-gray-800">
                                    <div className="flex items-center gap-3">
                                        <Clock className="w-5 h-5 text-gray-600" />
                                        <span className="text-sm font-medium">Tidak Aktif</span>
                                    </div>
                                    <span className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                                        {stats?.armada.tidak_aktif || 0}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-900/20 rounded-sm border border-orange-200 dark:border-orange-800">
                                    <div className="flex items-center gap-3">
                                        <FileWarning className="w-5 h-5 text-orange-600" />
                                        <span className="text-sm font-medium">Dokumen Segera Expired</span>
                                    </div>
                                    <span className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                                        {stats?.notifications.dokumen_expired_soon || 0}
                                    </span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Recent Activities */}
                    <Card className="industrial-card lg:col-span-2">
                        <CardHeader className="border-b border-border pb-4 flex flex-row items-center justify-between">
                            <CardTitle className="section-title text-lg">Work Order Terbaru</CardTitle>
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => navigate('/work-orders')}
                                className="text-xs uppercase tracking-wide"
                                data-testid="view-all-activities"
                            >
                                Lihat Semua <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                        </CardHeader>
                        <CardContent className="pt-4">
                            {activities.length > 0 ? (
                                <div className="space-y-3">
                                    {activities.slice(0, 5).map((activity, index) => (
                                        <div 
                                            key={activity.id || index}
                                            className="flex items-center justify-between p-3 bg-muted/30 rounded-sm hover:bg-muted/50 transition-colors cursor-pointer"
                                            onClick={() => navigate(`/work-orders`)}
                                            data-testid={`activity-${index}`}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-3">
                                                    <p className="text-sm font-medium truncate">{activity.judul}</p>
                                                    <StatusBadge status={activity.status} />
                                                </div>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="mono-label">{activity.work_order_number}</span>
                                                    <span className="text-muted-foreground">•</span>
                                                    <span className="text-xs text-muted-foreground">{activity.vehicle_nomor_polisi}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <PriorityBadge priority={activity.priority} />
                                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                                    {formatRelativeTime(activity.due_date)}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-muted-foreground text-center py-8">
                                    Belum ada aktivitas terbaru
                                </p>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </Layout>
    );
}
